/**
 * @fileoverview Validates template defaults for bulk inspection bypass mode.
 * @module inspection/bulkDefaultsValidator
 *
 * Responsibilities:
 *   - Simulate running the rules engine with all fields set to their default values.
 *   - Detect required fields that are visible after rule evaluation but have no default.
 *   - Detect conditional fields that become required via rules but lack defaults.
 *   - Produce a human-readable validation report the template author can review.
 *
 * Dependencies: rulesEngine (evaluateAllFormRules), bulkBypassDefaults (resolveBypassDefaults).
 *
 * Architecture:
 *   - Pure functions operating on form/field types — no database or DOM access.
 *   - Shared between tenant-web (template builder) and mobile-pwa (if needed).
 *   - Accepts BulkAnalysisForm for compatibility; when the underlying objects
 *     carry layout and rules (i.e. they are full InspectionForm instances passed
 *     through BulkAnalysisForm typing), the rules engine is used for deeper
 *     validation. Otherwise, only static default checks are performed.
 *
 * Notes:
 *   - This is advisory, not blocking. The template author can acknowledge and proceed.
 *   - Header and module forms are excluded from validation (not processed during bulk).
 */

import type { InspectionForm, FieldResponse, FieldType, InspectionFormLayout, InspectionRule } from './types';
import { evaluateAllFormRules } from './rulesEngine';
import { resolveBypassDefaults } from './bulkBypassDefaults';
import type { BulkAnalysisForm } from './bulkTemplateAnalysis';

// ---------------------------------------------------------------------------
// Validation result types
// ---------------------------------------------------------------------------

/**
 * @contract BulkDefaultsValidationWarning
 * Purpose: A single warning found when validating bulk defaults against rules.
 * Fields:  severity, message, form/field context.
 * Rules:   Advisory only — does not block template save.
 * Sync:    Ephemeral — computed on demand in the builder UI.
 */
export interface BulkDefaultsValidationWarning {
  severity: 'warning' | 'error';
  message: string;
  formName?: string;
  fieldLabel?: string;
  fieldId?: string;
  fieldType?: string;
}

/**
 * @contract BulkDefaultsValidationResult
 * Purpose: Complete validation report for bulk defaults.
 * Fields:  warnings array, pass/fail flag, summary string.
 * Rules:   pass = true when warnings is empty.
 * Sync:    Ephemeral.
 */
export interface BulkDefaultsValidationResult {
  pass: boolean;
  warnings: BulkDefaultsValidationWarning[];
  summary: string;
}

// ---------------------------------------------------------------------------
// Field type sets
// ---------------------------------------------------------------------------

const PROCESSABLE: ReadonlySet<FieldType> = new Set([
  'toggle', 'text', 'textarea', 'number', 'measurement',
  'dropdown', 'radioGroup', 'checkboxGroup',
  'date', 'dateTime',
]);

// ---------------------------------------------------------------------------
// Duck-type check for full InspectionForm shape
// ---------------------------------------------------------------------------

interface FormWithRulesSupport extends BulkAnalysisForm {
  layout?: InspectionFormLayout;
  rules?: InspectionRule[];
  initialVisibility?: string;
  version?: number;
  updatedAt?: string;
}

function isFullInspectionForm(form: BulkAnalysisForm): form is FormWithRulesSupport & InspectionForm {
  const candidate = form as FormWithRulesSupport;
  return (
    candidate.layout != null
    && typeof candidate.layout === 'object'
    && Array.isArray((candidate.layout as InspectionFormLayout).sections)
  );
}

// ---------------------------------------------------------------------------
// Core validation
// ---------------------------------------------------------------------------

/**
 * @process validateBulkDefaults
 *
 * Steps:
 * 1. Resolve bypass defaults from the forms.
 * 2. If forms carry layout+rules (full InspectionForm), run the rules engine
 *    with default responses and check visibility/required state post-rules.
 * 3. If forms are BulkAnalysisForm only (no layout), fall back to static
 *    field-level required+default checks without rules evaluation.
 * 4. Build result with warnings and summary.
 *
 * Inputs:  Array of BulkAnalysisForm (or full InspectionForm cast to it).
 * Outputs: BulkDefaultsValidationResult.
 * Failure Handling: Gracefully handles forms with no rules or fields.
 * Idempotency: NOT REQUIRED — date defaults use current time.
 */
export function validateBulkDefaults(
  forms: ReadonlyArray<BulkAnalysisForm>,
): BulkDefaultsValidationResult {
  const warnings: BulkDefaultsValidationWarning[] = [];

  const standardForms = forms.filter(
    (f) => f.formKind !== 'header' && f.formKind !== 'module',
  );

  if (standardForms.length === 0) {
    return { pass: true, warnings: [], summary: 'No standard forms to validate.' };
  }

  // Resolve bypass defaults
  const bypassDefaults = resolveBypassDefaults(standardForms);

  // Check if we can run the rules engine (forms have layout + rules)
  const canRunRules = standardForms.every(isFullInspectionForm);

  if (canRunRules) {
    // Build FieldResponse records for the rules engine
    const responses: Record<string, FieldResponse> = {};
    for (const [fieldId, bypass] of Object.entries(bypassDefaults)) {
      responses[fieldId] = { value: bypass.value };
    }

    const fullForms = standardForms as unknown as InspectionForm[];
    const rulesResult = evaluateAllFormRules(fullForms, responses);

    for (const form of fullForms) {
      const formState = rulesResult[form._id];
      const formName = form.name ?? 'Unnamed Form';

      if (formState?.hidden) continue;

      for (const field of form.fields) {
        if (!PROCESSABLE.has(field.type)) continue;

        const fieldState = formState?.fields?.[field.id];
        const isHidden = fieldState?.hidden ?? false;
        const isRequired = fieldState?.required ?? field.required ?? false;

        if (isHidden) continue;

        if (isRequired) {
          const bypass = bypassDefaults[field.id];
          const hasValue = bypass?.value != null && bypass.value !== '';

          if (!hasValue) {
            const becameRequired = !field.required && isRequired;
            warnings.push({
              severity: becameRequired ? 'error' : 'warning',
              message: becameRequired
                ? `"${field.label}" becomes required via rules when defaults are applied, but has no default value.`
                : `"${field.label}" is required but has no default value. It will be blank in bulk inspections.`,
              formName,
              fieldLabel: field.label,
              fieldId: field.id,
              fieldType: field.type,
            });
          }
        }
      }
    }
  } else {
    // Static check — no rules engine, just field-level required + default
    for (const form of standardForms) {
      const formName = form.name ?? 'Unnamed Form';

      for (const field of form.fields) {
        const fieldType = field.type as FieldType;
        if (!PROCESSABLE.has(fieldType)) continue;
        if (!(field.required ?? false)) continue;

        const bypass = bypassDefaults[field.id];
        const hasValue = bypass?.value != null && bypass.value !== '';

        if (!hasValue) {
          warnings.push({
            severity: 'warning',
            message: `"${field.label}" is required but has no default value. It will be blank in bulk inspections.`,
            formName,
            fieldLabel: field.label,
            fieldId: field.id,
            fieldType,
          });
        }
      }
    }
  }

  // Build summary
  const errorCount = warnings.filter((i) => i.severity === 'error').length;
  const warningCount = warnings.filter((i) => i.severity === 'warning').length;

  let summary: string;
  if (warnings.length === 0) {
    summary = 'All defaults are valid. Bulk bypass will produce complete responses.';
  } else {
    const parts: string[] = [];
    if (errorCount > 0) parts.push(`${errorCount} rule conflict${errorCount === 1 ? '' : 's'}`);
    if (warningCount > 0) parts.push(`${warningCount} missing default${warningCount === 1 ? '' : 's'}`);
    summary = parts.join(', ') + ' — review before enabling bulk bypass.';
  }

  return {
    pass: warnings.length === 0,
    warnings,
    summary,
  };
}
